import { autoinject, bindable, Container } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ListFieldTyped } from 'models/ListSettings';
import {
  CommentForEntity,
  isStrapArticleType,
  MooringArticleSearchResult,
  MooringCategoryType,
  MooringComponent,
  MooringComponentType,
  MooringGridLine,
} from 'models/mooring';
import { MooringArticleService, ToastService } from 'services';
import { MooringComponentSearchHandler, MooringOfferService, MooringService } from 'services/mooring';
import { toKg, toM, toChecked } from 'lib/ui/ValueDisplayFormatters';
import { MooringTableBase } from '../table-base';
import { TableEntryManager } from 'services/ui-helpers';
import { DialogService } from 'aurelia-dialog';
import { PubSub } from 'lib/event/PubSub';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { AsyncErrorHandler } from 'lib/ui';
import { TableManager } from '../TableManager';
import { calculateFactorsMbl } from '../factors';
import { SearchResult } from 'elements/search/search-input';
import { MooringSelectEntriesForPupulateArgs } from 'features/mooring/component-templates/mooring-select-entries-for-populate';
import { ensureNumberOrDefault } from 'lib/helpers';
import { FilterManager } from 'lib/tables/FilterManager';
import { Filters } from 'elements/Filter';
import { MaterialFactorService } from 'services/material-factor-service';
import { MaterialFactor } from 'models/mooring/MaterialFactor';
import { OfferViewContext } from '../../OfferViewContext';
import { CalculateUtilizationFactorDto, MooringStep } from 'models/mooring/UtilizationFactor';
import { MooringComponentSearchResult } from 'services/mooring/mooring-component-search-handler';

@autoinject
export class GridLinesTable extends MooringTableBase<MooringGridLine> {
  protected table = new TableManager('grid-lines-table', tableColumns);

  @bindable
  protected onSave: (args: { entries: MooringGridLine[]; canNavigateOnSave: boolean }) => void;

  protected context = OfferViewContext.useContext(this.container);

  constructor(
    protected container: Container,
    protected t: I18N,
    protected toast: ToastService,
    protected fm: FilterManager,
    dialogService: DialogService,
    mooringArticleService: MooringArticleService,
    // Used in HTML for article search
    mooringComponentSearchHandler: MooringComponentSearchHandler,
    tableEntryManager: TableEntryManager,
    pubsub: PubSub,
    confirmation: ConfirmDialog,
    mooringOfferService: MooringOfferService,
    mooringService: MooringService,
    materialFactorService: MaterialFactorService,
    materialFactors: MaterialFactor
  ) {
    super(
      'grid-line',
      dialogService,
      mooringComponentSearchHandler,
      tableEntryManager,
      mooringArticleService,
      pubsub,
      confirmation,
      mooringOfferService,
      mooringService,
      materialFactorService,
      materialFactors
    );

    pubsub.sub('tabs:change-guard', async () => {
      if (this.changeTracker.areEqual(this.entries)) {
        this.pubsub.publish('tabs:change-guard-ok', null);
        return;
      }

      const res = await this.confirmation.confirmSave();

      if (res == 'cancel') {
        return;
      } else if (res === 'discard-continue') {
        this.pubsub.publish('tabs:change-guard-ok', null);
      } else {
        this.onSave({ entries: this.entries, canNavigateOnSave: true });
      }
    });
  }

  async attached() {
    await super.attached();

    this.fm.init({
      context: 'mooring-grid-lines',
      storageKey: 'mooring-grid-lines',
      filters: {} satisfies Filters,
      defaultFilters: {},
    });
  }

  protected setOrderByColumn(column: string) {
    this.fm.setOrderByColumn(column);
  }

  protected onInputChange(index: number, name: (typeof tableColumns)[number]['field']) {
    const line = this.entries[index];

    if (name === 'Length') {
      this.setComponentsQuantity(line.Components, line);
    }

    if (name === 'IsAccident' || name === 'DimForce') {
      calculateFactorsMbl(line.DimForce, line, this.materialFactors, line.IsAccident);
      this.calculateUtilizationFactor(line.Components, line);
    }
  }

  protected async calculateUtilizationFactor(component: MooringComponent[], line: MooringGridLine) {
    const calculateUtilizationFactorDtos: CalculateUtilizationFactorDto[] = [];
    component.forEach((component) => {
      calculateUtilizationFactorDtos.push({
        Id: component.Id,
        HasKnot: component.HasKnot,
        MblRopes: line.MblRopes,
        MblSinkRopes: line.MblSinkRopes,
        MblStraps: line.MblStraps,
        MblChainShackleConnectionPlates: line.MblChainShackleConnectionPlates,
        MblUsedChainRopeWithKnot: line.MblUsedChainRopeWithKnot,
      });
    });

    const response = await this.mooringService.calculateUtilizationFactor({
      mooringId: this.mooringId,
      mooringStep: MooringStep.GRID_LINE,
      dto: calculateUtilizationFactorDtos,
    });

    response?.data.forEach((element) => {
      component.find((x) => x.Id === element.Id).UtilizationFactor = element.UtilizationFactor;
    });
  }

  protected onElementSelected(
    type: MooringComponentType,
    item: { id: number; title: string },
    line: MooringGridLine,
    entry: keyof MooringGridLine
  ) {
    if (type == 'buoy' && entry === 'StartBuoyId') {
      line.StartBuoyId = item.id;
      line.StartBuoyNo = item.title;
    }

    if (type == 'buoy' && entry === 'EndBuoyId') {
      line.EndBuoyId = item.id;
      line.EndBuoyNo = item.title;
    }
  }

  protected save() {
    this.onSave({
      entries: this.entries,
      canNavigateOnSave: false,
    });
  }

  protected override postPasteAdjustment(index: number, name: (typeof tableColumns)[number]['field']) {
    this.onInputChange(index, name);
  }

  protected setComponentQuantity(component: MooringComponent, line: MooringGridLine) {
    const strapLengths = line.Components.reduce((acc, curr) => {
      if (isStrapArticleType(curr.TypeVariant)) {
        acc += curr.PrimaryDimension || 0;
      }
      return acc;
    }, 0);

    if (component.CategoryType === MooringCategoryType.ROPE) {
      component.Quantity = ensureNumberOrDefault(line.Length - strapLengths, 1);
    }
  }

  protected setComponentsQuantity(components: MooringComponent[], line: MooringGridLine) {
    const strapLengths = line.Components.reduce((acc, curr) => {
      if (isStrapArticleType(curr.TypeVariant)) {
        acc += curr.PrimaryDimension || 0;
      }
      return acc;
    }, 0);

    components.forEach((component) => {
      if (component.CategoryType === MooringCategoryType.ROPE) {
        component.Quantity = ensureNumberOrDefault(line.Length - strapLengths, 1);
      }
    });
  }

  protected onArticleSelected(searchResult: MooringComponentSearchResult, component: MooringComponent, line: MooringGridLine) {
    const res = searchResult.return;
    if (!res) return;
    component.ArticleProductId = res.Id;
    component.MBL = res.MBL;
    component.ProductName = res.ProductName;
    component.CategoryName = res.Category;
    component.TypeName = res.Type;
    component.ProductTypeName = res.ProductType;
    component.PrimaryDimension = res.PrimaryDimension;
    component.SecondaryDimension = res.SecondaryDimension;
    component.CategoryType = res.CategoryType;
    component.TypeVariant = res.TypeVariant;

    this.setComponentQuantity(component, line);

    this.onSave({
      entries: this.entries,
      canNavigateOnSave: false,
    });
  }

  protected onAddComponent() {
    const highestIndex = Math.max(0, ...this.entries.map((x) => x.SortIndex));
    this.entries.push({
      AnalysisNo: ``,
      SortIndex: highestIndex + 1,
    } as MooringGridLine);
  }

  protected moveGridLine(gridLine: MooringGridLine, direction: 'up' | 'down') {
    const index = this.entries.indexOf(gridLine);
    const newEntriesOrder = [...this.entries];

    if (direction === 'up' && index > 0) {
      [newEntriesOrder[index - 1], newEntriesOrder[index]] = [newEntriesOrder[index], newEntriesOrder[index - 1]];

      newEntriesOrder[index].SortIndex = index;
      newEntriesOrder[index - 1].SortIndex = index - 1;
    } else if (direction === 'down' && index < newEntriesOrder.length - 1) {
      [newEntriesOrder[index + 1], newEntriesOrder[index]] = [newEntriesOrder[index], newEntriesOrder[index + 1]];

      newEntriesOrder[index].SortIndex = index;
      newEntriesOrder[index + 1].SortIndex = index + 1;
    }

    this.entries = newEntriesOrder;
  }

  protected getEntriesForPopulate(): MooringSelectEntriesForPupulateArgs {
    const entries = structuredClone(this.entries);
    return {
      groups: [
        {
          _identifier: 'grid-lines',
          title: this.t.tr('mooring.gridLine_plural'),
          options: entries
            .sort((a, b) => a.AnalysisNo.localeCompare(b.AnalysisNo))
            .map((x) => ({ name: x.AnalysisNo || '', id: x.Id, selected: false, articles: x.Components?.length || 0 })),
        },
      ],
    };
  }

  @AsyncErrorHandler
  protected async onDelete(index: number) {
    const res = await this.confirmation.confirmDelete();
    if (!res) return;

    const entry = this.entries[index];
    if (!entry) return;

    if (entry.Id) {
      await this.mooringService.deleteGridLines(this.mooringId, entry.Id);
    }

    this.entries.splice(index, 1);
    this.toast.showDeleted();
  }

  protected addEntryComment(component: MooringGridLine) {
    this.openCommentDialog({
      mooringId: this.mooringId,
      isReadOnly: false,
      componentId: component.Id,
      componentType: CommentForEntity.GridLine,
    });
  }

  protected addComponentComment(component: MooringComponent) {
    this.openCommentDialog({
      mooringId: this.mooringId,
      isReadOnly: false,
      componentId: component.Id,
      componentType: CommentForEntity.GridLineComponent,
    });
  }

  protected async clearForm() {
    const approved = await this.confirmation.confirmYesCancel('mooring.clearForm', 'mooring.clearFormConfirmation');

    if (approved) {
      const restedTable = this.resetFields(this.entries, fieldsToReset);
      this.entries = restedTable;
    }
  }

  protected resetFields<T extends Record<string, any>>(gridLines: T[], fieldsToReset: (keyof T)[]): T[] {
    return gridLines.map((gridLines) => {
      const updatedGridLine = { ...gridLines };

      fieldsToReset.forEach((field) => {
        const value = updatedGridLine[field];

        if (typeof value === 'boolean') {
          (updatedGridLine[field] as boolean) = false;
        } else {
          updatedGridLine[field] = null;
        }
      });

      return updatedGridLine;
    });
  }

  protected unbind() {
    this.pubsub.unsub();
    super.unbind();
  }

  protected detached() {
    this.pubsub.unsub();
  }
}

const fieldsToReset: (keyof MooringGridLine)[] = [
  'DimForce',
  'IsAccident',
  'MblRopes',
  'MblSinkRopes',
  'MblStraps',
  'MblChainShackleConnectionPlates',
  'MblUsedChainRopeWithKnot',
];

const tableColumns: ListFieldTyped<MooringGridLine>[] = [
  {
    field: 'AnalysisNo',
    title: 'mooring.analysisNo',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: (value: string) => value,
    inputType: 'text',
  },
  {
    field: 'StartBuoyNo',
    title: 'mooring.buoy',
    selected: true,
    visible: true,
    filterType: 'TEXT',
  },
  {
    field: 'EndBuoyNo',
    title: 'mooring.buoy',
    selected: true,
    visible: true,
    filterType: 'TEXT',
  },
  {
    field: 'Length',
    title: 'mooring.length',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toM,
    inputType: 'float',
  },
  {
    field: 'DimForce',
    title: 'mooring.dimForce',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'IsAccident',
    title: 'mooring.isAccident',
    helpText: 'mooring.dimForceDescription',
    selected: true,
    visible: true,
    filterType: 'BOOL',
    transform: toChecked,
    inputType: 'bool',
  },
  {
    field: 'MblRopes',
    title: 'mooring.minMblRope',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblSinkRopes',
    title: 'mooring.minMblSinkRope',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblStraps',
    title: 'mooring.minMblStrap',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblChainShackleConnectionPlates',
    title: 'mooring.minMblChain',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblUsedChainRopeWithKnot',
    title: 'mooring.minMblUsedChainRopeWithKnot',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'actions',
    title: '',
    selected: true,
    visible: true,
    filterType: 'none',
  },
];
